import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Listing, LISTING_TIERS, ListingTier } from 'models/Listing';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ListingCardTierPreview from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/components/ListingCardTierPreview';
import api from 'api';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import RadioSliderField from 'components/RadioSliderField';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './ChangeListingTierModal.styles.scss';
import './ChangeListingTierModal.styles.responsive.scss';

type ChangeListingTierModalProps = {
  className?: string;
  listing: Partial<Listing>;
  onChange: (listing: Partial<Listing>) => void;
};

const ChangeListingTierModal: React.ForwardRefRenderFunction<
  OverlayRef,
  ChangeListingTierModalProps
> = (props, ref) => {
  const { className, listing, onChange } = props;

  const classes = classNames('change-listing-tier-modal', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  useImperativeHandle(ref, () => modal, [modal]);

  const createSelectOptions = useCreateSelectOptions();

  const slideSteps = useMemo(
    () =>
      createSelectOptions(
        Object.values(LISTING_TIERS).filter((el) => el !== listing?.tier),
      ),
    [listing?.tier, createSelectOptions],
  );

  const initialValues = useMemo(
    () => ({ listingTier: slideSteps?.[0]?.value }),
    [slideSteps],
  );

  const onSubmit = useCallback(
    async (values: { listingTier: ListingTier }) => {
      const { listingTier } = values;

      try {
        const { data } = await api.portfolio.admin.changeTier(
          listing?._id,
          listingTier,
        );

        onChange(data);
        modal.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [listing?._id, modal, onChange, popServerError],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('changeListingTier')}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, values: { listingTier } }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="listingTier"
              component={RadioSliderField}
              slideSteps={slideSteps}
            />

            <ListingCardTierPreview listing={listing} showAs={listing.tier} />

            <ArrowdownIcon />

            <ListingCardTierPreview listing={listing} showAs={listingTier} />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {t('confirm')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(ChangeListingTierModal);
